import { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { socket } from '../../socket';
import HomeDashboardChartsGrid from './HomeDashboardChartsGrid';
import useStatisticsDashboardCharts, { useHomeDashboardCharts } from '../statistics/charts/board/hooks/useStatisticsDashboardCharts';
import { clearDashboardChartBatchCache } from '../statistics/charts/board/hooks/useDashboardChartsBatch';
import { useChartWorkbenchLauncher } from '../../components/navbar/top/ChartWorkbenchLauncher';
import { canAccessHomeDashboardSection } from '../../utils/dashboard/homeDashboardSectionAccess';
import { HOME_DASHBOARD_SECTION_IDS } from '../../utils/dashboard/homeDashboardLayout';

const HomeDashboardChartsSection = ({ layoutEditing = false }) => {
	const { userInfo } = useSelector((state) => state.auth);
	const canViewHomeCharts = canAccessHomeDashboardSection(userInfo, HOME_DASHBOARD_SECTION_IDS.HOME_CHARTS);
	const canEditHomeCharts = canViewHomeCharts;
	const [actionMessage, setActionMessage] = useState('');
	const [dataRefreshToken, setDataRefreshToken] = useState(0);
	const { charts, deleteChart, reorderCharts, renameChart, resizeChartColSpan, maxCharts, isChartsLoading } = useHomeDashboardCharts(
		userInfo?._id,
		{ enabled: canViewHomeCharts },
	);
	const { charts: statisticsCharts, isChartsLoading: isStatisticsChartsLoading } = useStatisticsDashboardCharts(userInfo?._id, { enabled: canEditHomeCharts });
	const chartWorkbenchLauncher = useChartWorkbenchLauncher();

	useEffect(() => {
		if (!canViewHomeCharts) return undefined;

		if (!socket.connected) {
			socket.connect();
		}

		const handleRefreshStats = (payload) => {
			if (payload?.scope === 'finalCostVerification' || payload?.scope === 'processingOnly') return;
			clearDashboardChartBatchCache();
			setDataRefreshToken((token) => token + 1);
		};

		socket.on('refresh dashboard stats', handleRefreshStats);

		return () => {
			socket.off('refresh dashboard stats', handleRefreshStats);
		};
	}, [canViewHomeCharts]);

	const showMessage = useCallback((message) => {
		setActionMessage(message);
		window.setTimeout(() => setActionMessage(''), 3200);
	}, []);

	const handleOpenChartWorkbench = useCallback(
		(snapshot) => {
			if (!chartWorkbenchLauncher?.canOpen || typeof chartWorkbenchLauncher.openEdit !== 'function') {
				showMessage('当前无法打开图表工作台');
				return;
			}
			const sourceId = snapshot?.sourceStatisticsChartId;
			const sourceAlive = Boolean(sourceId) && statisticsCharts.some((chart) => chart.id === sourceId);
			const chartId = sourceId && (isStatisticsChartsLoading || sourceAlive) ? sourceId : snapshot?.id;
			if (!chartId) return;
			chartWorkbenchLauncher.openEdit(chartId);
		},
		[chartWorkbenchLauncher, isStatisticsChartsLoading, showMessage, statisticsCharts],
	);

	const handleRename = useCallback(
		(chartId, title) => {
			const result = renameChart(chartId, title);
			if (result?.reason === 'empty') {
				showMessage('名称不能为空');
			}
			return result;
		},
		[renameChart, showMessage],
	);

	const handleResize = useCallback((chartId, colSpan, maxColumns) => resizeChartColSpan(chartId, colSpan, maxColumns), [resizeChartColSpan]);

	if (!canViewHomeCharts) return null;

	// 首页区块布局编辑时关掉图内拖拽/缩放，避免截走外层区块的 drop。
	const allowChartInteract = canEditHomeCharts && !layoutEditing;

	return (
		<div className='dashboard-home-charts-section'>
			<HomeDashboardChartsGrid
				charts={charts}
				onDelete={allowChartInteract ? deleteChart : undefined}
				onRename={allowChartInteract ? handleRename : undefined}
				onReorder={allowChartInteract ? reorderCharts : undefined}
				onResizeColSpan={allowChartInteract ? handleResize : undefined}
				onOpenChartWorkbench={allowChartInteract ? handleOpenChartWorkbench : undefined}
				onEditSaved={showMessage}
				isManager={canViewHomeCharts}
				maxCharts={maxCharts}
				actionMessage={actionMessage}
				isChartsLoading={isChartsLoading}
				dataRefreshToken={dataRefreshToken}
				userId={userInfo?._id}
			/>
		</div>
	);
};

export default memo(HomeDashboardChartsSection);

HomeDashboardChartsSection.propTypes = {
	layoutEditing: PropTypes.bool,
};
